/**
 * The stop before a quote is generated: items with nothing picked would be
 * left off the quote, and vendors asked who have not answered yet are the
 * reason to wait. The operator proceeds or goes back to Sourcing.
 */
import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import type { QuoteReadiness } from "./quoteReadiness";

type Props = {
  open: boolean;
  readiness: QuoteReadiness | null;
  /** The verb on the confirm button ("Generate quote", "Send the quote now"). */
  action: string;
  busy?: boolean;
  onProceed: () => void;
  onCancel: () => void;
};

const when = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export const GenerateQuoteCheckDialog = ({ open, readiness, action, busy, onProceed, onCancel }: Props) => {
  const leftOut = readiness?.leftOut || [];
  const noAnswers = !!readiness && readiness.vendorsAsked > 0 && readiness.vendorsAnswered === 0;
  const deadline = when(readiness?.deadlineAt || null);
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-2 sm:scale-95">
              <Dialog.Panel className="w-full max-w-lg transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-start justify-between">
                  <Dialog.Title className="text-lg font-bold text-[#00062e]">
                    {leftOut.length ? `${leftOut.length} item${leftOut.length === 1 ? "" : "s"} would be left out of the quote` : "No vendor has answered yet"}
                  </Dialog.Title>
                  <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-4 space-y-3 text-sm text-gray-700">
                  {leftOut.length > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="font-medium text-amber-900">
                        {leftOut.length === 1 ? "This item has" : "These items have"} no item selected, so the quote will not include {leftOut.length === 1 ? "it" : "them"}:
                      </p>
                      <ul className="mt-1 list-disc pl-5 text-amber-900">
                        {leftOut.map((l) => (
                          <li key={l.lineId}>{l.label}</li>
                        ))}
                      </ul>
                      <p className="mt-2 text-xs text-amber-800">
                        Pick a replacement in Sourcing (a vendor's quote or a close match) to include {leftOut.length === 1 ? "it" : "them"}, or proceed and the customer gets a partial quote.
                      </p>
                    </div>
                  )}
                  {noAnswers && readiness && (
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                      <p className="font-medium text-indigo-900">
                        {readiness.vendorsAsked} vendor{readiness.vendorsAsked === 1 ? "" : "s"} asked, 0 answered{deadline ? `, deadline ${deadline}` : ""}.
                      </p>
                      <p className="mt-1 text-xs text-indigo-900">
                        Still waiting on {readiness.vendorsPending.join(", ") || "the vendors"}. Generating now quotes from the platform's close matches; when a vendor answers later with a better price or a missing item, the RFQ flags a lower price option and you can issue the next quote version.
                      </p>
                    </div>
                  )}
                  {!noAnswers && readiness && readiness.vendorsAsked > 0 && readiness.vendorsPending.length > 0 && (
                    <p className="text-xs text-gray-600">
                      {readiness.vendorsAnswered} of {readiness.vendorsAsked} vendors answered; still waiting on {readiness.vendorsPending.join(", ")}.
                    </p>
                  )}
                </div>

                <div className="mt-5 flex flex-wrap justify-end gap-2">
                  <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-[#00062e] hover:bg-gray-50">
                    Cancel and go back to the selection
                  </button>
                  <button type="button" disabled={busy} onClick={onProceed} className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50">
                    {busy ? "Working..." : `${action} anyway`}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
